import { useState, useEffect } from "react";
import legalApi from "../../api/legalApi";
import "./LegalEntityForm.css";

const INITIAL_FORM = {
    // Основная информация
    inn: "",
    shortCompanyName: "",
    // fullCompanyName: "",
    kpp: "",
    ogrn: "",
    contactPhone: "",
    email: "",
    country: "Россия",
    contactPerson: "",
    // Юридический адрес
    legalAddress: "",
    // Почтовый адрес
    postDeliveryType: "address", // "address" | "po_box"
    postCity: "",
    postStreet: "",
    postIndex: "",
    postBuilding: "",
    postPoBox: "",
    // Платёжные реквизиты
    bankName: "",
    bik: "",
    checkingAccount: "",
    corrAccount: "",
    bankType: "not_selected" // "not_selected" | "bank" | "sbp" | "sbp_b2b"
};

const COUNTRIES = [
    "Россия", "Беларусь", "Казахстан", "Армения", "Кыргызстан", "Узбекистан", "Другая"
];

export default function LegalEntityForm({ userId, onPaymentSuccess }) {
    const [form, setForm] = useState(INITIAL_FORM);
    const [legalEntityId, setLegalEntityId] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [paymentLoading, setPaymentLoading] = useState(false);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [planInfo, setPlanInfo] = useState(null);
    const [step, setStep] = useState(1); // 1 - форма реквизитов, 2 - выбор оплаты

    // Загрузка существующих реквизитов и информации о тарифе
    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const [legalRes, planRes] = await Promise.all([
                    legalApi.get(userId),
                    legalApi.getPlanInfo()
                ]);

                //console.log('[LegalEntityForm] legalRes:', legalRes.data);
                //console.log('[LegalEntityForm] planRes:', planRes.data);

                if (legalRes.data?.exists && legalRes.data?.data) {
                    setForm(prev => ({ ...prev, ...legalRes.data.data }));
                    setLegalEntityId(legalRes.data.data.id);
                }

                if (planRes.data?.success) {
                    setPlanInfo(planRes.data);
                } else {
                    setPlanInfo({
                        planKey: 'pro_12_months',
                        planName: 'Pro 12 месяцев',
                        price: 69990,
                        durationMonths: 12
                    });
                }
            } catch (err) {
                console.error("Ошибка загрузки данных:", err);
                setPlanInfo({
                    planKey: 'pro_12_months',
                    planName: 'Pro 12 месяцев',
                    price: 69990,
                    durationMonths: 12
                });
            } finally {
                setLoading(false);
            }
        };

        if (userId) fetchData();
    }, [userId]);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setForm(prev => ({ ...prev, [name]: value }));
        setError(null);
        setSuccess(null);
    };

    // Генерация почтового адреса для отображения
    const getPostalAddressPreview = () => {
        if (form.postDeliveryType === "po_box") {
            return form.postPoBox ? `А/я ${form.postPoBox}, ${form.postIndex || ''}, ${form.postCity || ''}`.trim() : '';
        }
        const parts = [form.postIndex, form.postCity, form.postStreet, form.postBuilding].filter(Boolean);
        return parts.join(', ');
    };

    const validateForm = () => {
        if (!form.inn || !/^\d{10}$|^\d{12}$/.test(form.inn)) {
            setError("ИНН должен содержать 10 или 12 цифр");
            return false;
        }
        if (!form.shortCompanyName.trim()) {
            setError("Введите название организации");
            return false;
        }
        // if (!form.fullCompanyName.trim()) {
        //     setError("Введите полное название организации");
        //     return false;
        // }
        if (!form.kpp || !/^\d{9}$/.test(form.kpp)) {
            setError("КПП должен содержать 9 цифр");
            return false;
        }
        if (!form.contactPhone.trim()) {
            setError("Введите телефон");
            return false;
        }
        if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) {
            setError("Введите корректный email");
            return false;
        }
        if (!form.legalAddress.trim()) {
            setError("Введите юридический адрес");
            return false;
        }
        // Проверка почтового адреса
        if (form.postDeliveryType === "address") {
            if (!form.postCity.trim()) {
                setError("Введите населённый пункт");
                return false;
            }
            if (!form.postStreet.trim()) {
                setError("Введите улицу");
                return false;
            }
            if (!form.postIndex.trim()) {
                setError("Введите почтовый индекс");
                return false;
            }
            if (!form.postBuilding.trim()) {
                setError("Введите дом/корпус");
                return false;
            }
        } else {
            if (!form.postPoBox.trim()) {
                setError("Введите номер абонентского ящика");
                return false;
            }
        }
        // Проверка типа оплаты
        if (form.bankType === 'not_selected') {
            setError("Выберите тип оплаты");
            return false;
        }
        // Проверка банковских реквизитов для типа "Банк"
        if (form.bankType === 'bank') {
            if (!form.bankName || !form.bankName.trim()) {
                setError("Введите название банка");
                return false;
            }
            if (!form.bik || !/^\d{9}$/.test(form.bik)) {
                setError("БИК должен содержать 9 цифр");
                return false;
            }
            if (!form.checkingAccount || !/^\d{20}$/.test(form.checkingAccount)) {
                setError("Расчётный счёт должен содержать 20 цифр");
                return false;
            }
        }
        return true;
    };

    const handleSave = async () => {
        if (!validateForm()) return;

        setSaveLoading(true);
        setError(null);

        try {
            const response = await legalApi.save({ userId, ...form });
            if (response.data?.success) {
                setLegalEntityId(response.data.legalEntityId);
                setSuccess("Реквизиты сохранены");
                setStep(2);
            } else {
                setError(response.data?.error || "Ошибка сохранения");
            }
        } catch (err) {
            setError(err.response?.data?.error || "Ошибка сохранения реквизитов");
        } finally {
            setSaveLoading(false);
        }
    };

    const handlePaymentCard = async () => {
        //console.log('[LegalEntityForm] handlePaymentCard called');

        if (!userId) {
            setError("userId не определён");
            return;
        }
        if (!legalEntityId) {
            setError("Сначала сохраните реквизиты");
            return;
        }
        if (!planInfo?.planKey) {
            setError("Информация о тарифе не загружена");
            return;
        }

        setPaymentLoading(true);
        setError(null);

        try {
            const response = await legalApi.paymentCard(userId, legalEntityId, planInfo.planKey);
            if (response.data?.success && response.data?.paymentUrl) {
                window.location.href = response.data.paymentUrl;
            } else {
                setError(response.data?.error || "Ошибка создания платежа");
            }
        } catch (err) {
            setError(err.response?.data?.error || "Ошибка при создании платежа");
        } finally {
            setPaymentLoading(false);
        }
    };

    const handlePaymentInvoice = async () => {
        //console.log('[LegalEntityForm] handlePaymentInvoice called');

        if (!userId) {
            setError("userId не определён");
            return;
        }
        if (!legalEntityId) {
            setError("Сначала сохраните реквизиты");
            return;
        }
        if (!planInfo?.planKey) {
            setError("Информация о тарифе не загружена");
            return;
        }

        setPaymentLoading(true);
        setError(null);

        try {
            const response = await legalApi.paymentInvoice(userId, legalEntityId, planInfo.planKey);
            if (response.data?.success) {
                setSuccess(`Счёт №${response.data.invoiceNumber} сформирован и отправлен на email`);
                if (onPaymentSuccess) {
                    const link = document.createElement('a');
                    link.href = `data:application/pdf;base64,${response.data.pdfBase64}`;
                    link.download = `invoice-${response.data.invoiceNumber}.pdf`;
                    link.click();
                    onPaymentSuccess()
                };
            } else {
                setError(response.data?.error || "Ошибка формирования счёта");
            }
        } catch (err) {
            setError(err.response?.data?.error || "Ошибка при формировании счёта");
        } finally {
            setPaymentLoading(false);
        }
    };

    if (loading) {
        return <div className="legal-loading">Загрузка...</div>;
    }

    return (
        <div className="legal-entity-form">
            <div className="legal-header">
                <h2>Регистрация плательщика</h2>
                <p className="legal-subtitle">Юридическое лицо</p>
                {planInfo && (
                    <div className="plan-info-badge">
                        ПРО 12 месяцев — {planInfo.price?.toLocaleString('ru-RU')} ₽
                    </div>
                )}
            </div>

            {/* Шаги */}
            <div className="legal-steps">
                <div className={`step ${step >= 1 ? 'active' : ''}`}>
                    <span className="step-number">1</span>
                    <span className="step-label">Реквизиты</span>
                </div>
                <div className="step-line"></div>
                <div className={`step ${step >= 2 ? 'active' : ''}`}>
                    <span className="step-number">2</span>
                    <span className="step-label">Оплата</span>
                </div>
            </div>

            {error && <div className="legal-error">{error}</div>}
            {success && <div className="legal-success">{success}</div>}

            {step === 1 && (
                <div className="form-sections">
                    {/* Основная информация */}
                    <div className="form-section">
                        <h3 className="section-title">Основная информация</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label>ИНН: <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="inn"
                                    value={form.inn}
                                    onChange={handleChange}
                                    placeholder="пример: 7712345678"
                                    maxLength={12}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Название организации: <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="shortCompanyName"
                                    value={form.shortCompanyName}
                                    onChange={handleChange}
                                    placeholder="пример: ООО Когнитикс"
                                />
                                <span className="hint">без кавычек и организационно-правовой формы</span>
                            </div>
                        </div>

                        {/* <div className="form-row">
                            <div className="form-group">
                                <label>Полное название организации: <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="fullCompanyName"
                                    value={form.fullCompanyName}
                                    onChange={handleChange}
                                    placeholder='пример: ООО "Яндекс"'
                                />
                                <span className="hint">организационно-правовая форма — аббревиатурой</span>
                            </div>
                        </div> */}

                        <div className="form-row two-columns">
                            <div className="form-group">
                                <label>КПП: <span className="required">*</span></label>
                                <input
                                    type="text"
                                    name="kpp"
                                    value={form.kpp}
                                    onChange={handleChange}
                                    placeholder="пример: 771234567"
                                    maxLength={9}
                                />
                            </div>
                            <div className="form-group">
                                <label>ОГРН:</label>
                                <input
                                    type="text"
                                    name="ogrn"
                                    value={form.ogrn}
                                    onChange={handleChange}
                                    placeholder="пример: 1234567890123"
                                    maxLength={15}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Телефон: <span className="required">*</span></label>
                                <input
                                    type="tel"
                                    name="contactPhone"
                                    value={form.contactPhone}
                                    onChange={handleChange}
                                    placeholder="пример: +7 495 123-45-67"
                                />
                                <span className="hint">с кодом страны и кодом города</span>
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Электронная почта: <span className="required">*</span></label>
                                <input
                                    type="email"
                                    name="email"
                                    value={form.email}
                                    onChange={handleChange}
                                    placeholder="company@example.com"
                                />
                            </div>
                        </div>

                        <div className="form-row two-columns">
                            <div className="form-group">
                                <label>Страна:</label>
                                <select name="country" value={form.country} onChange={handleChange}>
                                    <option value="">выберите страну</option>
                                    {COUNTRIES.map(c => <option key={c} value={c}>{c}</option>)}
                                </select>
                                <span className="hint">используется только для отчётов</span>
                            </div>
                            <div className="form-group">
                                <label>Контактное лицо:</label>
                                <input
                                    type="text"
                                    name="contactPerson"
                                    value={form.contactPerson}
                                    onChange={handleChange}
                                    placeholder="ФИО"
                                />
                            </div>
                        </div>
                    </div>

                    {/* Юридический адрес */}
                    <div className="form-section">
                        <h3 className="section-title">Юридический адрес</h3>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Юридический адрес: <span className="required">*</span></label>
                                <textarea
                                    name="legalAddress"
                                    value={form.legalAddress}
                                    onChange={handleChange}
                                    placeholder="Полный юридический адрес организации"
                                    rows={3}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Почтовый адрес */}
                    <div className="form-section">
                        <h3 className="section-title">Почтовый адрес</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Выберите вариант доставки: <span className="required">*</span></label>
                                <div className="radio-group">
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="postDeliveryType"
                                            value="address"
                                            checked={form.postDeliveryType === "address"}
                                            onChange={handleChange}
                                        />
                                        <span>по адресу</span>
                                    </label>
                                    <label className="radio-label">
                                        <input
                                            type="radio"
                                            name="postDeliveryType"
                                            value="po_box"
                                            checked={form.postDeliveryType === "po_box"}
                                            onChange={handleChange}
                                        />
                                        <span>абонентский ящик</span>
                                    </label>
                                </div>
                            </div>
                        </div>

                        {form.postDeliveryType === "address" ? (
                            <>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Населённый пункт: <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="postCity"
                                            value={form.postCity}
                                            onChange={handleChange}
                                            placeholder="пример: Москва"
                                        />
                                    </div>
                                </div>
                                <div className="form-row">
                                    <div className="form-group">
                                        <label>Улица: <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="postStreet"
                                            value={form.postStreet}
                                            onChange={handleChange}
                                            placeholder="пример: Льва Толстого ул"
                                        />
                                    </div>
                                </div>
                                <div className="form-row two-columns">
                                    <div className="form-group">
                                        <label>Почтовый индекс: <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="postIndex"
                                            value={form.postIndex}
                                            onChange={handleChange}
                                            placeholder="пример: 119021"
                                            maxLength={6}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <label>Дом/корпус: <span className="required">*</span></label>
                                        <input
                                            type="text"
                                            name="postBuilding"
                                            value={form.postBuilding}
                                            onChange={handleChange}
                                            placeholder="пример: д. 16, для Марии Ивановой"
                                        />
                                        <span className="hint">укажите дом, корпус, строение, офис</span>
                                    </div>
                                </div>
                            </>
                        ) : (
                            <div className="form-row two-columns">
                                <div className="form-group">
                                    <label>Почтовый индекс: <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="postIndex"
                                        value={form.postIndex}
                                        onChange={handleChange}
                                        placeholder="119021"
                                        maxLength={6}
                                    />
                                </div>
                                <div className="form-group">
                                    <label>Абонентский ящик: <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        name="postPoBox"
                                        value={form.postPoBox}
                                        onChange={handleChange}
                                        placeholder="Номер а/я"
                                    />
                                </div>
                            </div>
                        )}

                        {getPostalAddressPreview() && (
                            <div className="form-row">
                                <div className="form-group">
                                    <label>Почтовый адрес:</label>
                                    <div className="address-preview">{getPostalAddressPreview()}</div>
                                    <span className="hint">так будет выглядеть адрес на конверте</span>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Платёжные реквизиты */}
                    <div className="form-section">
                        <h3 className="section-title">Платёжные реквизиты</h3>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Банк:</label>
                                <input
                                    type="text"
                                    name="bankName"
                                    value={form.bankName}
                                    onChange={handleChange}
                                    placeholder="пример: ПАО Сбербанк"
                                />
                            </div>
                        </div>

                        <div className="form-row two-columns">
                            <div className="form-group">
                                <label>БИК:</label>
                                <input
                                    type="text"
                                    name="bik"
                                    value={form.bik}
                                    onChange={handleChange}
                                    placeholder="пример: 047712345"
                                    maxLength={9}
                                />
                            </div>
                            <div className="form-group">
                                <label>Расчётный счёт:</label>
                                <input
                                    type="text"
                                    name="checkingAccount"
                                    value={form.checkingAccount}
                                    onChange={handleChange}
                                    placeholder="20 цифр"
                                    maxLength={20}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Корр. счёт:</label>
                                <input
                                    type="text"
                                    name="corrAccount"
                                    value={form.corrAccount}
                                    onChange={handleChange}
                                    placeholder="20 цифр"
                                    maxLength={20}
                                />
                            </div>
                        </div>

                        <div className="form-row">
                            <div className="form-group">
                                <label>Тип оплаты: <span className="required">*</span></label>
                                <select name="bankType" value={form.bankType} onChange={handleChange}>
                                    <option value="not_selected">не выбрано</option>
                                    <option value="bank">Банк (оплата по счёту)</option>
                                    <option value="sbp">СБП (Система Быстрых Платежей)</option>
                                    <option value="sbp_b2b">СБП B2B</option>
                                </select>
                                <span className="hint">Банк — оплата по счёту, СБП — через Робокассу</span>
                            </div>
                        </div>
                    </div>

                    <div className="form-footer">
                        <p className="required-note">Все поля, отмеченные <span className="required">*</span>, обязательны для заполнения.</p>
                        <div className="form-actions">
                            <button
                                className="legal-btn primary"
                                onClick={handleSave}
                                disabled={saveLoading}
                            >
                                {saveLoading ? "Сохранение..." : "Зарегистрировать"}
                            </button>
                            <button
                                className="legal-btn secondary"
                                onClick={() => setForm(INITIAL_FORM)}
                                disabled={saveLoading}
                            >
                                Отмена
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {step === 2 && (
                <div className="payment-section">
                    <h3>Оплата</h3>

                    <div className="payment-options">
                        {/* СБП или СБП B2B — Робокасса */}
                        {(form.bankType === 'sbp' || form.bankType === 'sbp_b2b') && (
                            <div className="payment-option card" onClick={handlePaymentCard}>
                                <div className="payment-info">
                                    <h4>Оплата через Робокассу</h4>
                                    <p>Быстрая оплата через СБП</p>
                                </div>
                                <button
                                    className="legal-btn primary"
                                    disabled={paymentLoading}
                                >
                                    {paymentLoading ? "Переход к оплате..." : "Оплатить"}
                                </button>
                            </div>
                        )}

                        {/* Банк — оплата по счёту */}
                        {form.bankType === 'bank' && (
                            <div className="payment-option invoice">
                                <div className="payment-info">
                                    <h4>Оплата по счёту</h4>
                                    <p>Безналичный расчёт для юр. лиц</p>
                                </div>
                                <button
                                    className="legal-btn primary"
                                    onClick={handlePaymentInvoice}
                                    disabled={paymentLoading || saveLoading}
                                >
                                    {paymentLoading ? "Формирование счёта..." : "Сформировать счёт"}
                                </button>
                            </div>
                        )}
                    </div>

                    <button
                        className="legal-btn link"
                        onClick={() => setStep(1)}
                    >
                        ← Вернуться к реквизитам
                    </button>
                </div>
            )}
        </div>
    );
}
